import React, { createContext, useContext, useEffect, useState } from 'react';
import { StringParam, useQueryParam } from 'use-query-params';
import useAsyncEffect from 'use-async-effect';

import {
  PriceTestOptions,
  PriceTestResponse,
  usePriceTestReportApi,
} from '@/features/details/price-test-reports/api';
import { usePagination } from '@/webapi/use-pagination';
import { Experience } from '@/webapi/use-experience-api';
import { DatePickerContext } from '@/features/details/date-pick/context';
import { findActiveVariants } from '@/features/details/summary/context';
import { MetricKind } from '@/webapi/models';

interface VariantPriceOffset {
  OffsetType: string;
  Offset: number;
  pid: string;
}

interface PriceOffsetList {
  list?: VariantPriceOffset[];
  variantId: string;
}

export interface PriceOffsetResponse {
  list: PriceOffsetList[];
}

type PriceTestReportsContextType = {
  loading: boolean;
  data?: PriceTestResponse;
  total?: number;
  page?: number;
  maxPages?: number;
  setPage?: (n: number) => void;
  setArgs?: (args: PriceTestOptions) => void;
  version: number;
  activeVariants: string[];
  primaryGoal?: MetricKind;
  priceOffsets?: PriceOffsetResponse;
};

export const PriceTestReportsContext =
  createContext<PriceTestReportsContextType>({
    loading: true,
    version: 0,
    activeVariants: [],
  });

const newPriceTestContext = (
  experience: Experience,
): PriceTestReportsContextType => {
  const { version, startDate, endDate } = useContext(DatePickerContext);
  const { getReportData, priceTestPrices } = usePriceTestReportApi();
  const [sessionType] = useQueryParam(`st`, StringParam);
  const [audience] = useQueryParam(`aud`, StringParam);
  const activeVariants = findActiveVariants(experience, version);
  const [init, setInit] = useState(false);
  useEffect(() => {
    if (!init) {
      setTimeout(() => {
        setInit(true);
      }, 200);
    }
  }, []);
  const { total, current, loading, page, maxPages, setPage, setArgs } =
    usePagination(
      getReportData,
      5,
      {
        startAt: startDate,
        endAt: endDate,
        expEndDate: experience?.endDate,
        version: version?.version,
        experienceId: experience.id,
        activeVariants,
        sessionType,
        audience,
        init,
      },
      true,
    );

  useEffect(() => {
    if (init) {
      setArgs({
        startAt: startDate,
        endAt: endDate,
        expEndDate: experience?.endDate,
        version: version?.version,
        experienceId: experience.id,
        activeVariants,
        sessionType,
        audience,
        init,
      });
    }
  }, [
    version?.version,
    sessionType,
    audience,
    activeVariants.join(``),
    init,
    startDate,
    endDate,
  ]);
  const primaryGoal = gePrimaryGoal(experience);
  const productIds = current?.products?.map((x) => x.pid);
  const [priceOffsets, setPriceTestOffsets] = useState<PriceOffsetResponse>();
  useAsyncEffect(
    async (isActive) => {
      if (productIds?.length === 0) {
        return;
      }
      const resp: PriceOffsetResponse = await priceTestPrices(
        productIds,
        experience.id,
      );
      if (isActive() && resp?.list?.length > 0) {
        setPriceTestOffsets(resp);
      }
    },
    [productIds?.sort()?.join(``)],
  );
  return {
    total,
    data: current,
    loading: loading || !init,
    page,
    maxPages,
    setPage,
    setArgs,
    version: version?.version,
    activeVariants,
    primaryGoal,
    priceOffsets,
  };
};
export const PriceTestReportsProvider = ({
  children,
  experience,
}: {
  children: any;
  experience: Experience;
}) => (
  <PriceTestReportsContext.Provider value={newPriceTestContext(experience)}>
    {children}
  </PriceTestReportsContext.Provider>
);

const gePrimaryGoal = (experience: Experience) => {
  switch (experience.primaryGoal) {
    case MetricKind.CONVERSION_RATE:
      return MetricKind.PRODUCT_CR;
    case MetricKind.REVENUE:
      return MetricKind.PRODUCT_REVENUE;
    case MetricKind.ADD_TO_CART_RATE:
      return MetricKind.PRODUCT_ATC_RATE;
    case MetricKind.RETURNS:
      return MetricKind.PRODUCT_RETURNS;
    case MetricKind.PER_SESSION_VALUE:
      return MetricKind.PRODUCT_PSV;
    case MetricKind.ITEMS_SOLD:
      return MetricKind.PRODUCT_ITEMS_SOLD;
    case MetricKind.PROFIT:
      return MetricKind.PRODUCT_PROFIT;
    case MetricKind.PROFIT_PER_SESSION:
      return MetricKind.PRODUCT_PROFIT_PER_SESSION;
    case MetricKind.TRANSACTIONS:
      return MetricKind.PRODUCT_TRANSACTIONS;
    case MetricKind.ITEMS_PER_PURCHASE:
      return MetricKind.PRODUCT_IPP;
    case MetricKind.PRODUCT_DETAIL_VIEWS:
      return MetricKind.PRODUCT_DETAILS_VIEWS_RATE;
    default:
      return MetricKind.PRODUCT_CR;
  }
};
